import {
  Typography, CircularProgress, Stack, Alert, useMediaQuery,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
} from 'react';
import { Box, Container, styled } from '@mui/system';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import JsxParser from 'react-jsx-parser';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Form } from 'react-final-form';
import { useTheme } from '@emotion/react';
import { useConfirm } from 'material-ui-confirm';
import get from 'lodash.get';

import {
  createInviteGql,
  getInviteFormByPathGql,
  getInviteGql,
  updateInviteGql,
} from './gql';
import ParticipantPrompt from './components/ParticipantPrompt';
import MainParticipantPrompt from './components/MainParticipantPrompt';
import TableSeatingPlan from './components/TableSeatingPlan';
import AttendanceConfirmation from './components/AttendanceConfirmation';
import StepButtons from './components/StepButtons';
import LinkedStepper from './components/LinkedStepper';
import CompactStepper from './components/CompactStepper';
import Toc from './components/Toc';
import SwitchForm from './components/SwitchForm';
import { GeneralContext } from '../../util/Store';
import TableSizePrompt from './components/TableSizePrompt';
import NotFound from '../../Components/NotFound';
import RsvpHeading from '../../Components/RsvpHeading';
import { findMissingTableAssignments, mapSeatingIntoAttendees, shouldHideStep } from './helper';
import FormPrompt from './components/FormPrompt';
import FormContainer from '../../Components/FormContainer';
import GlobalStyles from '../../Components/GlobalStyles';
import { ThemeContext } from '../../util/ThemeStore';
import DynamicSummary from './components/DynamicSummary';
import ExternalLink from '../../Components/ExternalLink';
import Error404Page from '../404';
import BookedOut from '../BookedOut';
import useSendInviteProgress from './useSendInviteProgress';

const ContentContainer = styled(Container)({
  // padding: '10vh 42px 16px 42px',
});

const buildDefaultRsvpStepperItems = (visibleSteps) => visibleSteps.map((step) => ({
  id: step.id || step.title,
  label: step.params?.stepperTitle || step.title,
}));

const ECOMMERCE_STEP_LABELS = {
  'steps.chooseTicket': 'Ticket auswählen',
  'steps.enterAttendee': 'Teilnehmer eingeben',
  'steps.invoiceAddress': 'Rechnungsadresse',
  'steps.summary': 'Zusammenfassung',
};

const buildLinkedRsvpStepperItems = (stepperConfig) => {
  const allSteps = Array.isArray(stepperConfig?.steps) ? [...stepperConfig.steps] : [];
  if (!allSteps.length) return null;
  return allSteps
    .sort((a, b) => (a.globalIndex || 0) - (b.globalIndex || 0))
    .map((step, index) => {
      const hardcodedLabel = step.titleKey ? ECOMMERCE_STEP_LABELS[step.titleKey] : null;
      const label = step.title || hardcodedLabel || step.id || `step_${index + 1}`;
      return {
        id: step.id || `${step.owner || 'step'}_${index}`,
        label,
        owner: step.owner,
      };
    });
};

export default function InviteResponse() {
  const snackbar = useSnackbar();
  const { inviteCode, path } = useParams();
  const [attendees, setAttendees] = useState([]);
  const [steps, setSteps] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const generalStore = useContext(GeneralContext);
  const themeStore = useContext(ThemeContext);
  const theme = useTheme();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const navigateKeepingParams = (targetPath) => {
    const query = searchParams.toString();
    navigate(query ? `${targetPath}?${query}` : targetPath);
  };
  const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [formValues, setFormValues] = useState({});
  const formRef = useRef();
  const contentRef = useRef();
  const scrollAfterResizeRef = useRef(false);
  const isIframe = useMemo(() => window.self !== window.top, []);
  const trackProgress = useSendInviteProgress();

  const scrollToTop = () => {
    const scrollOpts = { top: 0, behavior: 'smooth' };
    window.scrollTo(scrollOpts);
    document.documentElement.scrollTo(scrollOpts);
    document.body.scrollTo(scrollOpts);
    contentRef.current?.scrollIntoView({ behavior: 'smooth' });
    if (isIframe) {
      scrollAfterResizeRef.current = true;
      window.parent.postMessage({ type: 'scrollToIframeTop' }, '*');
    }
  };

  useEffect(() => {
    if (!isIframe) return undefined;
    const el = contentRef.current;
    if (!el) return undefined;
    const sendResize = () => {
      const height = el.offsetTop + el.offsetHeight + 20;
      window.parent.postMessage(
        { type: 'iframeResize', height, scrollToTop: scrollAfterResizeRef.current },
        '*',
      );
      scrollAfterResizeRef.current = false;
    };
    const observer = new ResizeObserver(sendResize);
    observer.observe(el);
    observer.observe(document.body);
    return () => observer.disconnect();
  }, [isIframe]);

  const isFirstStepRender = useRef(true);
  useEffect(() => {
    if (isFirstStepRender.current) {
      isFirstStepRender.current = false;
      return;
    }
    scrollToTop();
    if (isIframe && contentRef.current) {
      const height = contentRef.current.offsetTop + contentRef.current.offsetHeight + 20;
      window.parent.postMessage({ type: 'iframeResize', height, scrollToTop: true }, '*');
    }
  }, [activeStep, isIframe]); // scrollToTop intentionally excluded — stable closure over refs

  const {
    data: inviteFormResult,
    loading: formLoading,
    error: inviteFormError,
  } = useQuery(getInviteFormByPathGql, {
    fetchPolicy: 'no-cache',
    variables: { path },
    onCompleted: (result) => {
      const form = result?.getInviteFormByPath?.inviteForm;
      generalStore.setFooterLinks(form?.frontendSettings?.footerLinks);
      if (result?.getInviteFormByPath?.themeConfig?.typography) {
        themeStore.setTypography(result?.getInviteFormByPath?.themeConfig?.typography);
      }
      setSteps(form.elements);
    },
  });

  const {
    data,
    loading,
  } = useQuery(getInviteGql, {
    fetchPolicy: 'no-cache',
    variables: { inviteCode },
    skip: !inviteCode,
    onCompleted: ({ getInvite }) => {
      trackProgress(inviteCode, { type: 'open' });
      if (/* document.cookie.includes('isAdmin=true') && */ (searchParams.has('update') || getInvite.status === 'created')) {
        setSteps(getInvite.inviteForm.elements);
        return;
      }

      if (getInvite.status === 'accepted') {
        navigateKeepingParams(`/${path}/response/${inviteCode}/accepted`);
        return;
      }
      if (getInvite.status === 'declined') {
        navigateKeepingParams(`/${path}/response/${inviteCode}/declined`);
        return;
      }

      // navigate(`/einladung/${inviteCode}/closed`);
      setSteps(getInvite.inviteForm.elements);
    },
    onError: () => snackbar.showMessage('Fehler beim laden.'),
  });

  const invite = useMemo(() => data?.getInvite || {}, [data?.getInvite]);
  const inviteFormByPath = inviteFormResult?.getInviteFormByPath;
  const inviteForm = useMemo(() => inviteFormByPath?.inviteForm, [inviteFormByPath?.inviteForm]);
  const attributeQuotas = useMemo(() => inviteFormByPath?.attributeQuotas || {}, [inviteFormByPath?.attributeQuotas]);
  const isBookedOut = inviteFormByPath?.isBookedOut === true;
  const inviteFormStatusCode = inviteFormError?.networkError?.statusCode || inviteFormError?.statusCode;
  useEffect(() => {
    if (!data?.getInvite) return;
    setAttendees(data?.getInvite.attendees);
    generalStore.setContactInfo(data?.getInvite?.inviteForm?.frontendSettings?.contactInfo);
  }, [data?.getInvite]);

  const initialValues = useMemo(() => {
    if (!data?.getInvite) {
      return {
        attendees: [],
        eventTables: [],
      };
    }
    const attendees = data?.getInvite.attendees;

    const seating = [];
    if (data?.getInvite.seat !== undefined && data?.getInvite.seat !== null && data?.getInvite.seatTableId !== undefined) {
      if (!seating[data.getInvite.seatTableId]) seating[data.getInvite.seatTableId] = [];
      seating[data.getInvite.seatTableId || 0][data.getInvite.seat - 1] = 'main';
    }
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const index in attendees) {
      const attendee = attendees[index];
      if (attendee.seat === undefined) continue;
      if (!seating[attendee.eventTableId || 0]) seating[attendee.eventTableId || 0] = [];
      seating[attendee.eventTableId || 0][attendee.seat - 1] = attendee.id || attendee.tempId;
    }

    const switchFormElement = data?.getInvite.inviteForm.elements.find(({ type }) => type === 'switchForm');
    let switchForm = null;
    if (switchFormElement && data?.getInvite.status === 'declined') {
      switchForm = 'inviteDecline:';
    }

    return {
      title: data?.getInvite.title,
      forename: data?.getInvite.forename,
      name: data?.getInvite.name,
      company: data?.getInvite.company,
      email: data?.getInvite.email,
      status: data?.getInvite.status,
      eventTable: data?.getInvite.eventTable ? { seatCount: data?.getInvite.eventTable?.seatCount?.toString() } : undefined,
      eventTables: data?.getInvite.eventTables?.length
        ? data?.getInvite.eventTables.map((table) => { table.seatCount = table.seatCount.toString(); return table; })
        : [{ seatCount: null }],
      customAttributes: (data?.getInvite.customAttributes || {}),
      attendees,
      seating,
      switchForm,
    };
  }, [data?.getInvite]);

  const visibleSteps = useMemo(() => steps.filter((step) => {
    const show = step.isVisible !== false && !shouldHideStep(formValues, step?.params?.hideConditions);
    if (show) return show;
    const fieldNames = (step?.params?.customFieldDefinition || [])
      .filter(({ name }) => name).map(({ name }) => name);

    // if field is hidden then reset the value
    for (const fieldName of fieldNames) {
      if (!get(formValues, fieldName) || !formRef.current) continue;
      formRef.current.change(fieldName, undefined);
    }
    return show;
  }), [steps, formValues]);
  const linkedStepperConfig = inviteForm?.frontendSettings?.stepperConfig;
  const isLinkedFlow = !!inviteForm?.frontendSettings?.isEcommerceLinked && !!linkedStepperConfig;
  const linkedStepperItems = isLinkedFlow ? buildLinkedRsvpStepperItems(linkedStepperConfig) : null;
  const stepperItems = linkedStepperItems || buildDefaultRsvpStepperItems(visibleSteps);
  const linkedCutoverStart = linkedStepperConfig?.cutover?.firstRsvpGlobalIndex || 1;
  const stepperActiveStep = isLinkedFlow
    ? Math.max(0, linkedCutoverStart - 1 + activeStep)
    : activeStep;

  // Derive the active step id for LinkedStepper from the rsvp portion of linkedStepperItems
  const rsvpLinkedSteps = useMemo(
    () => (linkedStepperItems || []).filter((s) => s.owner !== 'ecommerce'),
    [linkedStepperItems],
  );
  const activeLinkedStepId = rsvpLinkedSteps[activeStep]?.id;

  const [updateInvite, { loading: updateInviteLoading }] = useMutation(updateInviteGql, {
    onError: (err) => {
      console.error(err);
      navigateKeepingParams(`/${path}/response/${inviteCode}/error`);
    },
  });

  const [createInvite, { loading: createInviteLoading }] = useMutation(createInviteGql);

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ mt: 5 }}>
        <CircularProgress sx={{ color: 'white' }} />
      </Stack>
    );
  }

  const renderFormElements = (element, inviteForm, values) => {
    const submitType = invite?.status === 'created' ? 'create' : 'update';
    switch (element.type) {
      case 'participantPrompt':
        return (
          <ParticipantPrompt
            {...element}
            invite={invite}
            attendees={attendees}
            submitType={submitType}
            inviteForm={inviteForm}
          />
        );
      case 'mainParticipantPrompt':
        return (
          <MainParticipantPrompt
            {...element}
            steps={steps}
            invite={invite}
            attendees={attendees}
            submitType={submitType}
            inviteForm={inviteForm}
            attributeQuotas={attributeQuotas}
          />
        );
      case 'tableSeatingPlan':
        return (
          <TableSeatingPlan
            {...element}
            invite={invite}
            attendees={attendees}
            submitType={submitType}
            inviteForm={inviteForm}
          />
        );
      case 'attendanceConfirmation':
        return (
          <AttendanceConfirmation
            {...element}
            invite={invite}
            attendees={attendees}
            submitType={submitType}
            inviteForm={inviteForm}
          />
        );
      case 'tableSizePrompt':
        return (
          <TableSizePrompt
            {...element}
            invite={invite}
            attendees={attendees}
            submitType={submitType}
            inviteForm={inviteForm}
          />
        );
      case 'toc':
        return (
          <Toc
            {...element}
            invite={invite}
            attendees={attendees}
            submitType={submitType}
            inviteForm={inviteForm}
          />
        );
      case 'switchForm':
        return (
          <SwitchForm
            {...element}
            invite={invite}
            attendees={attendees}
            onStepChange={(newSteps) => setSteps(newSteps)}
            submitType={submitType}
            inviteForm={inviteForm}
          />
        );
      case 'formPrompt':
        return (
          <FormPrompt
            {...element}
            inviteForm={inviteForm}
          />
        );
      case 'summaryConfirmation':
        return (
          <DynamicSummary
            {...element}
            invite={invite}
            attendees={attendees}
            submitType={submitType}
            inviteForm={inviteForm}
            inviteValues={values}
          />
        );
      default:
        return (<h1>{element.type}</h1>);
    }
  };

  const mapAttendees = (attendee, data) => {
    let companionIndex;
    if (attendee.companionTempId) {
      companionIndex = data.attendees.findIndex(({ tempId }) => tempId === attendee.companionTempId);
    }
    return {
      id: attendee.id,
      title: attendee.title,
      forename: attendee.forename,
      name: attendee.name,
      email: attendee.email || data.email,
      customAttributes: attendee.customAttributes,
      seat: attendee.seat,
      seatTable: attendee.seatTable,
      waitingList: attendee.waitingList || false,
      companionIndex: companionIndex !== -1 ? companionIndex : undefined,
      ticketCategory: attendee?.ticketCategory?.id ? { id: attendee.ticketCategory.id } : undefined,
    };
  };

  const onSubmit = async (data, visibleSteps) => {
    const currentSteps = visibleSteps || steps;
    const currentStep = currentSteps[activeStep];
    if (!currentStep) return;

    const elementId = currentStep.id;
    const trackElementProgress = (progressInviteCode) => {
      if (elementId === undefined || elementId === null) return;
      trackProgress(progressInviteCode, { type: 'element', elementId });
    };
    const trackSubmitProgress = (progressInviteCode, status) => trackProgress(
      progressInviteCode,
      { type: 'submit', status },
    );
    const hasAttendanceConfirmation = currentSteps.find(({ type }) => type === 'attendanceConfirmation');
    const defaultStatus = hasAttendanceConfirmation ? data.status : 'accepted';
    const finalStatus = data.customAttributes?.switchForm?.startsWith('inviteDecline')
      ? 'declined'
      : defaultStatus;

    if (
      (currentStep.type === 'attendanceConfirmation' && data.status === 'declined')
      || (data.customAttributes && data.customAttributes.switchForm?.startsWith('inviteDecline'))
    ) {
      await updateInvite({
        variables: {
          inviteCode,
          input: {
            status: 'declined',
            attendees: data.attendees?.map((attendee) => mapAttendees(attendee, data)),
            customAttributes: data.customAttributes,
          },
        },
      });
      trackElementProgress(inviteCode);
      trackSubmitProgress(inviteCode, 'declined');
      navigateKeepingParams(`/${path}/response/${inviteCode}/declined`);
      return;
    }

    if (currentStep.type === 'mainParticipantPrompt' && currentStep.description && currentStep?.params?.showModal) {
      await confirm({
        title: '',
        hideCancelButton: true,
        allowClose: false,
        description: (
          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography>
              <JsxParser
                components={{ Typography, Stack, Link: ExternalLink }}
                jsx={currentStep.description}
              />
            </Typography>
          </Alert>
        ),
        confirmationText: 'Weiter',
      });
    }

    if (currentStep.type === 'tableSeatingPlan') {
      const copiedData = { ...data };
      mapSeatingIntoAttendees(copiedData, steps);
      const missingAssignments = findMissingTableAssignments(copiedData);
      if (missingAssignments.length) {
        await confirm({
          title: 'Fehlende Sitzplatzzuweisung',
          hideCancelButton: true,
          allowClose: false,
          description: (
            <Alert severity="error" sx={{ mt: 3 }}>
              <Typography>
                Folgenden Personen wurde noch kein Sitzplatz zugewiesen:
              </Typography>
              <ul>
                {missingAssignments.map(({ forename, name }) => (
                  <li>
                    {forename}
                    {' '}
                    {name}
                  </li>
                ))}
              </ul>
              <Typography>
                Bitte kehren Sie zurück und nehmen die Zuweisung vor.
              </Typography>
            </Alert>
          ),
          confirmationText: 'Zurück',
        });
        return;
      }
    }

    if (activeStep !== (currentSteps.length - 1)) {
      trackElementProgress(inviteCode);
      setActiveStep((curr) => curr + 1);
      return;
    }

    mapSeatingIntoAttendees(data, steps);

    const mainParticipantPromptStep = steps.find(({ type }) => type === 'mainParticipantPrompt');
    const tableSizePromptStep = steps.find(({ type }) => type === 'tableSizePrompt');
    let mainParticipant = {};
    if (mainParticipantPromptStep) {
      mainParticipant = {
        title: data.title,
        forename: data.forename,
        name: data.name,
        company: data.company,
        email: data.email,
        seat: data.seat,
        seatTable: data.seatTable,
        customAttributes: data.customAttributes,
      };
    }

    if (!inviteCode) {
      const { data: createResult } = await createInvite({
        variables: {
          path,
          input: {
            ...mainParticipant,
            attendees: data.attendees?.map((attendee) => mapAttendees(attendee, data)),
          },
        },
      });
      const createdInviteCode = createResult.createInvite.token;
      trackElementProgress(createdInviteCode);
      trackSubmitProgress(createdInviteCode, finalStatus);
      navigateKeepingParams(`/${path}/response/${createdInviteCode}/${finalStatus === 'declined' ? 'declined' : 'accepted'}`);
      return;
    }

    await updateInvite({
      variables: {
        inviteCode,
        input: {
          ...mainParticipant,
          status: finalStatus,
          eventTable: tableSizePromptStep ? data.eventTable : undefined,
          eventTables: tableSizePromptStep ? data.eventTables : undefined,
          waitingList: data.waitingList || false,
          attendees: data.attendees?.map((attendee) => mapAttendees(attendee, data)),
        },
      },
    });
    trackElementProgress(inviteCode);
    trackSubmitProgress(inviteCode, finalStatus);
    navigateKeepingParams(`/${path}/response/${inviteCode}/${finalStatus === 'declined' ? 'declined' : 'accepted'}`);
  };

  if (inviteFormStatusCode === 404) {
    return <Error404Page />;
  }

  if (formLoading) {
    return (
      <ContentContainer maxWidth="lg">
        <Stack alignItems="center" sx={{ mt: 5 }}>
          <CircularProgress />
        </Stack>
      </ContentContainer>
    );
  }

  if (isBookedOut && !inviteCode) {
    return (
      <ContentContainer maxWidth="lg">
        <RsvpHeading logoUrl={inviteFormByPath?.logoUrl} inviteForm={inviteForm} />
        <GlobalStyles
          customCss={inviteFormByPath?.customCss}
          colors={inviteFormByPath?.colors}
          font={inviteFormByPath?.font}
        />
        <FormContainer sx={{ pb: 2, mb: 2 }} elevation={4} matchesSm={matchesSm}>
          <BookedOut />
        </FormContainer>
      </ContentContainer>
    );
  }

  if (!inviteFormResult) {
    return (
      <ContentContainer maxWidth="lg">
        <FormContainer sx={{ pb: 2, mb: 2 }} elevation={4} matchesSm={matchesSm}>
          <NotFound />
        </FormContainer>
      </ContentContainer>
    );
  }

  let stepperNode;
  if (isLinkedFlow) {
    stepperNode = (
      // ── Two-phase bar for linked checkout+RSVP flows ──
      <LinkedStepper
        linkedSteps={linkedStepperItems}
        activeStepId={activeLinkedStepId}
        theme={theme}
      />
    );
  } else if (matchesSm) {
    // ── Compact progress bar for mobile — avoids label wrap/overflow ──
    stepperNode = <CompactStepper activeStep={activeStep} steps={stepperItems} />;
  } else {
    // ── Standard MUI stepper for standalone RSVP ──
    stepperNode = (
      <Stepper activeStep={stepperActiveStep} alternativeLabel sx={{ mb: 4 }}>
        {stepperItems.map((step) => (
          <Step key={step.id}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
    );
  }

  return (
    <ContentContainer ref={contentRef} maxWidth="lg">
      <RsvpHeading logoUrl={inviteFormResult?.getInviteFormByPath?.logoUrl} inviteForm={inviteForm} />
      <GlobalStyles
        customCss={inviteFormResult?.getInviteFormByPath?.customCss}
        colors={inviteFormResult?.getInviteFormByPath?.colors}
        font={inviteFormResult?.getInviteFormByPath?.font}
      />
      <FormContainer sx={{ pb: 2, mb: 2 }} elevation={4} matchesSm={matchesSm}>
        {!inviteForm || (inviteForm.type !== 'registration' && !inviteCode) ? (
          <NotFound />
        ) : (
          <>
            {stepperNode}

            <Form
              onSubmit={(data) => onSubmit(data, visibleSteps)}
              initialValues={initialValues}
              render={({ handleSubmit, values, form }) => {
                setFormValues(values);
                useEffect(() => {
                  formRef.current = form;
                }, [form]);
                return (
                  <form
                    onSubmit={async (e) => {
                      await handleSubmit(e);
                      setTimeout(() => {
                        const firstError = document.querySelector('.Mui-error');
                        if (firstError) {
                          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          if (isIframe) {
                            const offsetY = firstError.getBoundingClientRect().top + window.scrollY - 100;
                            window.parent.postMessage({ type: 'scrollToIframeOffset', offsetY }, '*');
                          }
                        }
                      }, 100);
                    }}
                  >
                    {visibleSteps.map((step, index) => {
                      if (activeStep !== index) return null;
                      return (
                        <>
                          <Typography variant="h5">
                            {step.title}
                          </Typography>
                          {updateInviteLoading || createInviteLoading ? (
                            <Box sx={{
                              display: 'flex', flexDirection: 'column', alignItems: 'center', m: 6,
                            }}
                            >
                              <CircularProgress size={80} />
                            </Box>
                          ) : (
                            <>
                              {step.description && (
                                <Alert severity="info" sx={{ mt: 3 }}>
                                  <Typography>
                                    <JsxParser
                                      components={{ Typography, Stack, Link: ExternalLink }}
                                      jsx={step.description}
                                      bindings={{ values }}
                                    />
                                  </Typography>
                                </Alert>
                              )}
                              {step.params?.descriptionText && (
                                <Typography sx={{ textAlign: 'center' }}>
                                  <JsxParser
                                    components={{ Typography, Stack }}
                                    jsx={step.params?.descriptionText}
                                    bindings={{ values }}
                                    autoCloseVoidElements
                                  />
                                </Typography>
                              )}
                              {renderFormElements(step, inviteForm, values)}
                              <StepButtons
                                currentIndex={index}
                                onBackClick={() => setActiveStep((curr) => curr - 1)}
                                elements={visibleSteps}
                                currentElement={step}
                              />
                            </>
                          )}
                        </>
                      );
                    })}
                  </form>
                );
              }}
            />
          </>
        )}
      </FormContainer>
    </ContentContainer>
  );
}
